import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { distinctBy } from "../../utils/distinct-by";
import { createSectionController, setFlexControllerReferences } from "../../utils/flex-entity-utils";
import {
	Case,
	CoverageRequestTherapyDecision,
	FlexEntityType,
	FlexFormStatus,
	ImpossibleToEditAttribute,
	Maybe,
} from "../generated/graphql";
import { FlexContext } from "./flex-context";
import { FlexEntitySchema } from "./flex-entity-schema";
import { FlexFieldSchema } from "./flex-field-schema";
import { FlexSectionSchema } from "./flex-section-schema";
import { TypedFlexEntity } from "./typed-flex-entity";
import { defaultDateFormat, parseDate } from "@lyfegen-platform/utils";
import { addDays, format } from "date-fns";

const COVERAGE_DAYS_INCREASE = 1;

type CreateFlexContextArgs = {
	tenantId: string;
	entityType: FlexEntityType;
	flexEntity: Maybe<TypedFlexEntity<any>> | undefined;
	deserializedCases: Array<Case & { flexEntities: Array<TypedFlexEntity<any>> }>;
	dynamicParams: { [key: string]: string };
	isEditingSubmitted: boolean | undefined;
	isCopy: boolean;
	impossibleToEditAttributes: Array<ImpossibleToEditAttribute>;
	noPatients?: number | undefined;
};

const getSectionFields = (schema: FlexSectionSchema): Array<FlexFieldSchema> =>
	schema.fields.concat(schema.sections?.flatMap(getSectionFields) || []);

const FORMS_FOR_DECISION_CHANGE = ["CR-0000000001", "CE-0000000001", "RA-0000000001"];

export const createFlexContext = ({
	tenantId,
	entityType,
	flexEntity,
	deserializedCases,
	dynamicParams,
	isEditingSubmitted,
	isCopy,
	impossibleToEditAttributes,
	noPatients,
}: CreateFlexContextArgs): FlexContext => {
	const schema: FlexEntitySchema = JSON.parse(entityType.data);
	const fields = schema.sections.flatMap(getSectionFields);
	const areAllAttributesImpossibleToEdit =
		isEditingSubmitted &&
		schema.sections.flatMap(getSectionFields).length ===
			distinctBy(
				impossibleToEditAttributes.filter((attribute) =>
					fields.some((field) => field.attribute.identifier === attribute.identifier)
				),
				(attribute) => attribute.identifier
			).length;

	const context = {
		entityType,
		tenantId,
		flexEntity:
			flexEntity && isCopy
				? { ...flexEntity, data: transformCopiedData(entityType, flexEntity), id: undefined, status: undefined }
				: flexEntity,
		cases: deserializedCases,
		startingPoint: deserializedCases.flatMap((cs) => cs.flexEntitiesConnection.edges).find(({ isStartingPoint }) => isStartingPoint)
			?.node,
		isEditable:
			!areAllAttributesImpossibleToEdit &&
			(isEditingSubmitted || !flexEntity || flexEntity.status === FlexFormStatus.Draft || isCopy),
		schema,
		sectionControllers: schema.sections.map((section) => createSectionController(schema, entityType, section, "")),
		parameters: dynamicParams,
		impossibleToEditAttributes,
		isNegotiable: false,
		noPatients: noPatients,
		areAllAttributesImpossibleToEdit,
	} as FlexContext;
	context.sectionControllers.forEach((s) => setFlexControllerReferences(s, undefined, context));
	return context;
};

const transformCopiedData = (entityType: FlexEntityType, typedFlexEntity: TypedFlexEntity<any>) => {
	if (
		typedFlexEntity.data[AttributeIdentifiers.DecisionTherapy] !== CoverageRequestTherapyDecision.Trial ||
		!typedFlexEntity.data[AttributeIdentifiers.TherapyTrialEndDate] ||
		!FORMS_FOR_DECISION_CHANGE.includes(entityType.id)
	) {
		return typedFlexEntity.data;
	}

	return {
		...typedFlexEntity.data,
		[AttributeIdentifiers.DecisionTherapy]: CoverageRequestTherapyDecision.Approved,
		[AttributeIdentifiers.TherapyTrialStartDate]: undefined,
		[AttributeIdentifiers.TherapyTrialEndDate]: undefined,
		[AttributeIdentifiers.CoverageStartDate]: format(
			addDays(parseDate(typedFlexEntity.data[AttributeIdentifiers.TherapyTrialEndDate]), COVERAGE_DAYS_INCREASE),
			defaultDateFormat
		),
		[AttributeIdentifiers.CoverageEndDate]: undefined,
	};
};
